import { useQuery } from '@tanstack/react-query';

import collectionRepository, {
  getSectionProperties,
} from '@/features/collections/repositories/CollectionRepository';
import {
  type Collection,
  CollectionFilter,
  type DisplayedCollectionSection,
} from '@/features/collections/types/collection.types';
import { getPageMetadataProps } from '@/features/pages/repositories/PageRepository';
import { collectionKeys } from '@/shared/query/queryKeys';

export function fetchCollectionsByFilter(
  collectionFilter: CollectionFilter | 'all',
  tagFilter?: string
): Promise<Collection[]> {
  switch (collectionFilter) {
    case CollectionFilter.All:
    case 'all':
      return collectionRepository.getCollections();
    case CollectionFilter.Favourites:
      return collectionRepository.getFavouritedCollections();
    case CollectionFilter.Archived:
      return collectionRepository.getCollections(true);
    case CollectionFilter.Public:
      return collectionRepository.getPublicCollections();
    case CollectionFilter.Tagged:
      if (!tagFilter) return collectionRepository.getCollections();
      return collectionRepository.searchTaggedCollections(tagFilter);
    default:
      return collectionRepository.getCollections();
  }
}

export async function fetchCollectionStructure(collectionId: string) {
  const collection = await collectionRepository.getCollectionById(collectionId);
  if (!collection) return [];

  const collectionPageMetadata = await collectionRepository.getCollectionPageMetadata(collectionId);

  return (collection.sections || [])
    .filter(s => !s.archivedAt)
    .map<DisplayedCollectionSection>(section => ({
      section: {
        ...section,
        pages: collectionPageMetadata
          .filter(p => p.sectionId === section.id)
          .map(getPageMetadataProps),
      },
    }));
}

export function normalizeCollectionSections(collectionId: string) {
  return collectionRepository.getCollectionById(collectionId).then(collection => {
    if (!collection) return undefined;
    return {
      ...collection,
      sections: (collection.sections || []).map(getSectionProperties),
    };
  });
}

export function useCollectionsQuery(
  collectionFilter: CollectionFilter | 'all' = 'all',
  tagFilter?: string,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: collectionKeys.list(collectionFilter, tagFilter),
    queryFn: () => fetchCollectionsByFilter(collectionFilter, tagFilter),
    enabled: options.enabled,
  });
}

export function useCollectionStructure(collectionId?: string | null) {
  return useQuery({
    queryKey: collectionKeys.structure(collectionId || ''),
    queryFn: () => fetchCollectionStructure(collectionId || ''),
    enabled: !!collectionId,
  });
}
